import { Injectable } from '@angular/core';
import { Product } from '@models';
import { MapperFactory } from '@products/utils/mappers/mapper-factory';
import { ProductsMapper } from '@products/utils/mappers/product.type.mapper';
import { MapperParams } from '@products/utils/mappers/type.mapper';
import { SiteConfigService } from '@services';
import { ProductDTO } from '@types';
import { Logger } from '@utils/logger';
import { Apollo } from 'apollo-angular';
import { DocumentNode } from 'graphql';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  GraphQLFundDataService,
  ProductTypeParameter,
} from './graphql-fund-data.service';

const logger = Logger.getLogger('AllFundsPpssService');

export type AllFundsPPSSDTO = {
  [key in ProductTypeParameter]: ProductDTO[];
};
export type AllFundsPPSS = {
  [key in ProductTypeParameter]: Product[];
};

@Injectable({
  providedIn: 'root',
})
export class AllFundsPpssService extends GraphQLFundDataService {
  constructor(
    apollo: Apollo,
    siteConfigService: SiteConfigService,
    private mapperFactory: MapperFactory
  ) {
    super(apollo, siteConfigService);
  }

  /**
   * Invoke graphql API response for a given query.
   * @param query all funds graphql query
   */
  public register(query: DocumentNode): Observable<AllFundsPPSS> {
    return super.register(query, this.getVariables()).pipe(
      map((rawResponse) => rawResponse?.data), // Fetch the 'data' field consisting of all funds feed.
      map((allFundsPPSSDTO: AllFundsPPSSDTO) => {
        const mappedAllFundsPPSS: AllFundsPPSS = {
          cef: [],
          etf: [],
          if: [],
          mf: [],
          mmf: [],
          sma: [],
          tif: [],
          529: [],
          'variable-insurance': [],
          tof: [],
          uaff: [],
          cit: [],
        };

        // Iterate every product type parameter and map the response from 'Product' 'DTO' to its 'Model', as it is.
        Object.keys(allFundsPPSSDTO).forEach((productTypeParameter) => {
          const mapperParams: MapperParams = {
            config: this.siteConfigService,
            includeSoftClosed: false,
          };

          // Intentionally evaluated if-else for "non-matching" product type parameter.
          // As it contains the format not valid for the 'type' but valid for graphql query.
          if (productTypeParameter === 'variable_insurance') {
            mappedAllFundsPPSS[
              'variable-insurance'
            ] = this.mapperFactory
              .createMapper(ProductsMapper, mapperParams)
              .toDomain(allFundsPPSSDTO[productTypeParameter]);
          } else if (productTypeParameter === '_529') {
            mappedAllFundsPPSS[529] = this.mapperFactory
              .createMapper(ProductsMapper, mapperParams)
              .toDomain(allFundsPPSSDTO[productTypeParameter]);
          } else {
            mappedAllFundsPPSS[
              productTypeParameter
            ] = this.mapperFactory
              .createMapper(ProductsMapper, mapperParams)
              .toDomain(allFundsPPSSDTO[productTypeParameter]);
          }
        });

        logger.debug('Mapped all funds PPSS response: ', mappedAllFundsPPSS);

        // Return the mapped response object.
        return mappedAllFundsPPSS;
      })
    );
  }

  /**
   * Return required variables value to graphql query.
   */
  private getVariables(): Record<string, string> {
    return {
      countrycode: this.getCountry(),
      languagecode: this.getLanguage(),
      productTypecef: ProductTypeParameter.CLOSED_END_FUNDS,
      productTypeetf: ProductTypeParameter.ETFS,
      productTypeif: ProductTypeParameter.INTERVAL_FUNDS,
      productTypemf: ProductTypeParameter.MUTUAL_FUNDS,
      productTypemmf: ProductTypeParameter.MONEY_MARKET_FUNDS,
      productTypesma: ProductTypeParameter.SMAs,
      productTypetif: ProductTypeParameter.TIFs,
      productType529: ProductTypeParameter.PORTFOLIOS_529,
      productTypevariable_insurance: ProductTypeParameter.VARIABLE_INSURANCES,
      productTypecit: ProductTypeParameter.COLLECTIVE_INVESTMENT_TRUST,
    };
  }
}
